/**
 * @module SalesFlow/view
 */

import AtomicDevice from 'model/type/atomic-device';

/**
 * @TODO strg+x form device.ts make it a seperate file
 */
interface AttributeQuery {
    [index: string]: any;

    color: string;
    internalMemory: number;
    bundledWith?: string;
}

class Color {

    private _rgb: string;
    private _name: string;

    constructor (atomicDevice: AtomicDevice) {
        this._rgb = atomicDevice.attr.primaryColor;
        this._name = atomicDevice.attr.color;
    }

    get rgb (): string {
        return this._rgb;
    }

    get name (): string {
        return this._name;
    }

}

class ViewColor {

    private _color: Color;
    private _atomicDeviceId: number = undefined;
    private _isSelected: boolean = false;

    constructor (color: Color) {
        this._color = color;
    }

    public setSelected (selected: boolean): void {
        this._isSelected = selected;
    }

    public setAtomicDeviceId (id: number): void {
        this._atomicDeviceId = id;
    }

    public isSelected (): boolean {
        return this._isSelected;
    }

    public isAvailable (): boolean {
        if (undefined !== this._atomicDeviceId) {
            return true;
        }

        return false;

    }

    get rgb (): string {
        return this._color.rgb;
    }

    get name (): string {
        return this._color.name;
    }

    get atomicDeviceId (): number {
        return this._atomicDeviceId;
    }

    get cssClasses (): string {

        const cssClasses: string[] = [];

        // CO-18293: Colors are always selectable and automatically chooses the next available size
        // if (false === this.isAvailable()) {
        //     cssClasses.push('disabled');
        // }

        if (true === this.isSelected()) {
            cssClasses.push('selected');
        }

        // add en emmpty element to array to create leading space after reverse and join
        if (0 !== cssClasses.length) {
            cssClasses.push('');
        }

        return cssClasses.reverse().join(' ');

    }

}

/**
 * @internal
 */
export default class ViewColors {

    private _colors: ViewColor[] = [];

    constructor (atomicDevice: AtomicDevice) {

        const colors: Color[] = this.setColors(atomicDevice);
        this._colors = this.setViewColors(atomicDevice, colors);

    }

    private uniqueColors (colors: Color[]): Color[] {
        const uniqueColors: Color[] = [];

        const unique: any = {};

        for (const color of colors) {
            if (true === unique[color.name]) {
                continue;
            }

            uniqueColors.push(color);
            unique[color.name] = true;

        }

        return uniqueColors;
    }

    private sortColors (colors: Color[]): void {
        colors.sort((a: Color, b: Color) => {
            if (undefined !== a.name &&
                undefined !== b.name) {
                const nameA = a.name.toUpperCase();
                const nameB = b.name.toUpperCase();
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
            }

            return 0;
        });

    }

    /**
     * Map colors with atomicDevices with the very same size
     */
    private setViewColors (atomicDevice: AtomicDevice, colors: Color[]): ViewColor[] {

        const viewColors: ViewColor[] = colors.map((color) => {
            return new ViewColor(color);
        });

        const attributeQuery: AttributeQuery = {
            color: atomicDevice.attr.color,
            internalMemory: atomicDevice.attr.internalMemory
        };

        if (undefined !== atomicDevice.attr.bundledWith) {
            attributeQuery.bundledWith = atomicDevice.attr.bundledWith;
        }

        const atomicDevicesWithSameAttr = atomicDevice.device.getAtomicDevicesByAttr('internalMemory', atomicDevice.attr.internalMemory);

        for (const atomicDeviceWithSameAttr of atomicDevicesWithSameAttr) {
            for (const viewColor of viewColors) {

                if (viewColor.name !== atomicDeviceWithSameAttr.attr.color) {
                    continue;
                }

                if (undefined !== viewColor.atomicDeviceId) {
                    continue;
                }

                viewColor.setAtomicDeviceId(atomicDeviceWithSameAttr.id);

            }
        }

        for (const viewColor of viewColors) {
            if (viewColor.name !== atomicDevice.attr.color) {
                continue;
            }

            viewColor.setSelected(true);
            break;

        }

        return viewColors;

    }

    /**
     * Get colors from all atomicDevices
     */
    private setColors (atomicDevice: AtomicDevice): Color[] {
        let colors: Color[] = [];

        colors = atomicDevice.getDevice().getAtomicDevices().map((atomicDevice) => {
            return new Color(
                atomicDevice
            );

        });

        colors = this.uniqueColors(colors);

        this.sortColors(colors);

        return colors;

    }

    get all (): ViewColor[] {
        return this._colors;
    }

    get selected (): ViewColor {

        for (const color of this._colors) {
            if (color.isSelected()) {
                return color;
            }
        }

        return undefined;
    }

}
